import { FormEvent, useCallback, useEffect, useId, useState } from 'react';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { Markdown } from '../../ui/Markdown';
import { loc } from '../../lib/loc';
import { AdminFrame } from './AdminFrame';
import { errorText, fmtDate, rpc, type AppError } from './api';
import type { SchoolCtx } from './SchoolPage';
import { ReviewMark } from './ReviewMark';
import type { ComplianceDoc } from '../compliance/ComplianceList';

// ── Feature switches (platform settings) ───────────────────────────────────

export function FeatureSwitchesPanel() {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [list, setList] = useState<{ name: string; on: boolean }[] | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  useEffect(() => {
    rpc<{ name: string; on: boolean }[]>('platform_features').then((r) => (r.error ? setErr(r.error) : setList(r.data)));
  }, []);
  async function flip(name: string, on: boolean) {
    const r = await rpc<{ name: string; on: boolean }[]>('platform_set_feature', { p_name: name, p_on: on });
    if (r.error) return setErr(r.error);
    setList(r.data);
    announce(on ? t['fs.on'](t[`fs.${name}` as 'fs.playbook']) : t['fs.off'](t[`fs.${name}` as 'fs.playbook']));
  }
  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>{t['fs.title']}</h2>
      <p className="form-hint">{t['fs.hint']}</p>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      <ul className="sched-list">
        {list?.map((f) => (
          <li key={f.name}>
            <label className="check">
              <input type="checkbox" checked={f.on} onChange={(e) => flip(f.name, e.target.checked)} />
              <span>{t[`fs.${f.name}` as 'fs.playbook']}</span>
            </label>
            <Pill kind={f.on ? 'good' : 'neutral'}>{f.on ? t['fs.isOn'] : t['fs.isOff']}</Pill>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── School page: premium options, student languages, documents accepted ──

type Options = {
  entitlements: string[];
  student_languages: string[];
  safety_net_extra: Record<string, string>;
  session_max_minutes: number;
  acceptances: { key: string; title: Record<string, string>; version: number; accepted: { version: number; on: string; signatory: string } | null }[];
};

export function SchoolOptionsPanels(ctx: SchoolCtx) {
  const { t, lang, locale } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const school = ctx.detail.school.id;
  const [o, setO] = useState<Options | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const [langs, setLangs] = useState<string[]>([]);
  const [msgs, setMsgs] = useState<Record<string, string>>({});
  const [minutes, setMinutes] = useState(90);
  const [acc, setAcc] = useState<{ key: string; version: number; on: string; who: string } | null>(null);

  const load = useCallback(async () => {
    const r = await rpc<Options>('platform_school_options', { p_school_id: school });
    if (r.error) return setErr(r.error);
    setO(r.data);
    setLangs(r.data.student_languages);
    setMsgs(r.data.safety_net_extra);
    setMinutes(r.data.session_max_minutes);
  }, [school]);
  useEffect(() => {
    load();
  }, [load]);

  async function call(fn: string, args: Record<string, unknown>, ok: string) {
    setErr(null);
    const r = await rpc(fn, args);
    if (r.error) return setErr(r.error);
    announce(ok);
    load();
  }

  if (!o) return err ? <p className="error-text" role="alert">{errorText(t, err)}</p> : null;
  return (
    <>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      <div className="two-col two-col-even">
        <section className="panel p5" aria-labelledby={`${id}-ent`}>
          <h2 className="panel-title" id={`${id}-ent`}>{t['so.premium']}</h2>
          <p className="form-hint">{t['so.premiumHint']}</p>
          <div className="check-stack">
            {(['custom_playbook', 'insights_premium'] as const).map((e) => {
              const on = o.entitlements.includes(e);
              return (
                <label key={e} className="check">
                  <input type="checkbox" checked={on} onChange={() => call('platform_set_entitlement', { p_school_id: school, p_what: e, p_on: !on }, t['pa.saved'])} />
                  <span>{t[`so.ent.${e}` as 'so.ent.custom_playbook']}</span>
                </label>
              );
            })}
          </div>
          <form className="form" onSubmit={(ev: FormEvent) => { ev.preventDefault(); call('platform_set_session_limit', { p_school_id: school, p_minutes: minutes }, t['pa.saved']); }} noValidate>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-max`}>{t['so.sessionMax']}</label>
              <input id={`${id}-max`} className="field" type="number" min={10} max={240} value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} />
            </div>
            <div className="form-actions"><button type="submit" className="btn btn-ghost">{t['pa.save']}</button></div>
          </form>
        </section>
        <StudentLanguagesPanel ctx={ctx} langs={langs} setLangs={setLangs} msgs={msgs} setMsgs={setMsgs}
          onSave={() => call('platform_set_student_languages', { p_school_id: school, p_languages: langs, p_messages: msgs }, t['pa.saved'])} />
      </div>
      <section className="panel p5" aria-labelledby={`${id}-acc`}>
        <h2 className="panel-title" id={`${id}-acc`}>{t['so.docs']}</h2>
        <p className="form-hint">{t['so.docsHint']}</p>
        <ul className="sched-list">
          {o.acceptances.map((a) => (
            <li key={a.key}>
              <span>
                <strong>{loc(a.title, lang)}</strong> · v{a.version}{' '}
                {a.accepted ? <Pill kind={a.accepted.version === a.version ? 'good' : 'warn'}>{t['cp.accepted'](a.accepted.version)}</Pill> : <Pill kind="neutral">{t['cp.notAccepted']}</Pill>}
                {a.accepted && <span className="text-muted"> · {a.accepted.signatory} · {fmtDate(locale, a.accepted.on)}</span>}
              </span>
              <button type="button" className="link-btn" onClick={() => setAcc({ key: a.key, version: a.version, on: '', who: '' })}>{t['so.record']}<span className="visually-hidden"> – {loc(a.title, lang)}</span></button>
            </li>
          ))}
        </ul>
        {acc && (
          <form className="form" onSubmit={(ev: FormEvent) => { ev.preventDefault(); call('platform_record_acceptance', { p_school_id: school, p_key: acc.key, p_version: acc.version, p_on: acc.on || null, p_signatory: acc.who }, t['pa.saved']).then(() => setAcc(null)); }} noValidate>
            <div className="form-row">
              <div className="field-group">
                <label className="field-label" htmlFor={`${id}-on`}>{t['so.signedOn']}</label>
                <input id={`${id}-on`} className="field" type="date" value={acc.on} onChange={(e) => setAcc({ ...acc, on: e.target.value })} />
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor={`${id}-who`}>{t['so.signatory']}</label>
                <input id={`${id}-who`} className="field" value={acc.who} onChange={(e) => setAcc({ ...acc, who: e.target.value })} />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setAcc(null)}>{t['form.cancel']}</button>
              <button type="submit" className="btn btn-primary">{t['so.record']}</button>
            </div>
          </form>
        )}
      </section>
    </>
  );
}

// K5 (F2): the school's extra student languages, chosen from the catalog.
// Finnish, Swedish and English are always on and aren't listed. Each language
// shows whether its student screen is translated, and takes an optional
// safety-net message, which can be marked as checked by a native speaker.
type CatalogLang = {
  code: string;
  name_en: string;
  name_fi: string;
  name_sv: string;
  native_name: string;
  dir: 'ltr' | 'rtl';
  screen_status: 'reviewed' | 'draft' | 'none';
  source: string;
};

function StudentLanguagesPanel({ ctx, langs, setLangs, msgs, setMsgs, onSave }: {
  ctx: SchoolCtx;
  langs: string[];
  setLangs: (f: (p: string[]) => string[]) => void;
  msgs: Record<string, string>;
  setMsgs: (m: Record<string, string>) => void;
  onSave: () => void;
}) {
  const { t, lang } = useI18n();
  const id = useId();
  const [cat, setCat] = useState<CatalogLang[] | null>(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    rpc<CatalogLang[]>('platform_language_catalog').then((r) => setCat(r.error ? [] : r.data));
  }, []);
  if (!cat) return null;
  const name = (l: CatalogLang) => (lang === 'fi' ? l.name_fi : lang === 'sv' ? l.name_sv : l.name_en);
  const chosen = cat.filter((l) => langs.includes(l.code));
  const needle = q.trim().toLowerCase();
  const offer = needle
    ? cat.filter((l) => !langs.includes(l.code) && [l.name_en, l.name_fi, l.name_sv, l.native_name, l.code].some((x) => x.toLowerCase().includes(needle)))
    : [];
  const provisional = cat.some((l) => l.source === 'provisional');
  const saved = ctx.detail.school.safety_net;
  const reviewed = ctx.detail.school.safety_net_reviewed ?? {};

  return (
    <section className="panel p5" aria-labelledby={`${id}-lng`}>
      <h2 className="panel-title" id={`${id}-lng`}>{t['r4.langs.title']}</h2>
      <p className="form-hint">{t['r4.langs.hint']}</p>
      {provisional && <p className="form-hint"><Pill kind="warn">{t['ts.draft']}</Pill> {t['r4.langs.provisional']}</p>}
      <form className="form" onSubmit={(ev: FormEvent) => { ev.preventDefault(); onSave(); }} noValidate>
        <h3 className="overline">{t['r4.langs.chosen']}</h3>
        {chosen.length === 0 && <p className="text-muted">{t['r4.langs.none']}</p>}
        {chosen.map((l) => (
          <div key={l.code} className="field-group lang-row">
            <p>
              <strong lang={l.code}>{l.native_name}</strong> <span className="text-muted">({name(l)})</span>{' '}
              <Pill kind={l.screen_status === 'reviewed' ? 'good' : l.screen_status === 'draft' ? 'warn' : 'neutral'}>
                {t[`r4.langs.st.${l.screen_status}` as 'r4.langs.st.none']}
              </Pill>{' '}
              <button type="button" className="link-btn" onClick={() => setLangs((p) => p.filter((x) => x !== l.code))}>
                {t['r4.langs.remove'](name(l))}
              </button>
            </p>
            <label className="field-label" htmlFor={`${id}-sn-${l.code}`}>{t['r4.langs.netIn'](name(l))}</label>
            <textarea id={`${id}-sn-${l.code}`} className="field" rows={2} maxLength={600} lang={l.code} dir={l.dir}
              value={msgs[l.code] ?? ''} onChange={(e) => setMsgs({ ...msgs, [l.code]: e.target.value })} />
            {(saved[l.code] ?? '').trim() !== '' && (
              <ReviewMark ctx={ctx} lang={l.code} reviewed={!!reviewed[l.code]} changed={(msgs[l.code] ?? '').trim() !== (saved[l.code] ?? '').trim()} />
            )}
          </div>
        ))}
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-q`}>{t['r4.langs.search']}</label>
          <input id={`${id}-q`} className="field" type="search" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
        </div>
        {needle && offer.length === 0 && <p className="text-muted" role="status">{t['r4.langs.noMatch']}</p>}
        {offer.length > 0 && (
          <ul className="sched-list" aria-live="polite">
            {offer.slice(0, 12).map((l) => (
              <li key={l.code}>
                <span><span lang={l.code}>{l.native_name}</span> <span className="text-muted">({name(l)} · {t[`r4.langs.st.${l.screen_status}` as 'r4.langs.st.none']})</span></span>
                <button type="button" className="link-btn" onClick={() => { setLangs((p) => [...p, l.code]); setQ(''); }}>
                  {t['r4.langs.add'](name(l))}
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="form-hint">{t['r4.langs.netFallback']}</p>
        <div className="form-actions"><button type="submit" className="btn btn-primary">{t['r4.langs.save']}</button></div>
      </form>
    </section>
  );
}

// ── Platform compliance pack (publish versions) ────────────────────────────

type PDoc = ComplianceDoc & { accepted_by_schools: number };

export function PlatformCompliancePage() {
  const { t } = useI18n();
  return (
    <AdminFrame section="compliance" title={t['h2.complianceTitle']}>
      {() => <PlatformComplianceBody />}
    </AdminFrame>
  );
}

function PlatformComplianceBody() {
  const { t, lang, locale } = useI18n();
  const announce = useAnnounce();
  const [docs, setDocs] = useState<PDoc[] | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const [edit, setEdit] = useState<{ key: string; body: string; note: string; draft: boolean } | null>(null);
  const load = useCallback(async () => {
    const r = await rpc<PDoc[]>('platform_compliance');
    if (r.error) setErr(r.error);
    else setDocs(r.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  async function publish(e: FormEvent) {
    e.preventDefault();
    if (!edit) return;
    const cur = docs?.find((d) => d.key === edit.key);
    const r = await rpc('platform_publish_document', { p_key: edit.key, p_body: { ...(cur?.body ?? {}), en: edit.body }, p_change_note: edit.note, p_is_draft: edit.draft });
    if (r.error) return setErr(r.error);
    setEdit(null);
    announce(t['cp.published']);
    load();
  }
  if (!docs) return err ? <p className="error-text" role="alert">{errorText(t, err)}</p> : <p role="status">{t.loading}</p>;
  return (
    <>
      <p className="notice notice-block">{t['cp.adminIntro']}</p>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      <ul className="doc-list">
        {docs.map((d) => (
          <li key={d.key} className="panel p5">
            <div className="panel-head">
              <h2 className="panel-title">{loc(d.title, lang)}</h2>
              <span className="doc-pills">
                {d.is_draft && <Pill kind="warn">{t['ts.draft']}</Pill>}
                {d.blocked_reason && <Pill kind="crit">{t['cp.blocked']}</Pill>}
                {d.is_public && <Pill kind="neutral">{t['cp.public']}</Pill>}
              </span>
            </div>
            <p className="text-muted">
              {t['cp.version'](d.version, fmtDate(locale, d.published_at))}
              {d.requires_acceptance && <> · {t['cp.acceptedCount'](d.accepted_by_schools)}</>}
            </p>
            {edit?.key === d.key ? (
              <form className="form" onSubmit={publish} noValidate>
                <div className="field-group">
                  <label className="field-label" htmlFor={`b-${d.key}`}>{t['cp.bodyEn']}</label>
                  <textarea id={`b-${d.key}`} className="field mono" rows={14} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} />
                </div>
                <div className="field-group">
                  <label className="field-label" htmlFor={`n-${d.key}`}>{t['cp.changeNote']}</label>
                  <input id={`n-${d.key}`} className="field" value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} />
                </div>
                <label className="check">
                  <input type="checkbox" checked={edit.draft} onChange={(e) => setEdit({ ...edit, draft: e.target.checked })} />
                  <span>{t['cp.stillDraft']}</span>
                </label>
                <div className="form-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setEdit(null)}>{t['form.cancel']}</button>
                  <button type="submit" className="btn btn-primary" disabled={!edit.note.trim()}>{t['cp.publish'](d.version + 1)}</button>
                </div>
              </form>
            ) : (
              <>
                <details>
                  <summary>{t['cp.read']}</summary>
                  <Markdown text={loc(d.body, 'en')} />
                </details>
                <button type="button" className="link-btn" onClick={() => setEdit({ key: d.key, body: d.body.en ?? '', note: '', draft: d.is_draft })}>{t['cp.newVersion']}</button>
              </>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}
